import isNil from 'lodash-es/isNil';
import * as yup from 'yup';

import { DeviceServiceAdmin } from '@hems/service';
import { FIRMWARE_UPDATE_API_RESPONSE_CODE } from '@hems/util/src/constant/firmwareUpdate';
import { isNull } from '@hems/util/src/helper/helper';

import type { FirmwareUpdateValidateResponse } from 'hems/firmwareUpdate';

export const initYup = (): void => {
  yup.setLocale({
    mixed: {
      default: () => ({ key: 'message.invalid_value' }),
      required: ({ label }: { label?: string }) => ({ key: 'message.field_required', values: { label } }),
    },
    string: {
      email: ({ label }: { label?: string }) => ({ key: 'message.email_format', values: { label } }),
      min: ({ min, label }: { min: number; label?: string }) => ({
        key: 'message.longer_than_min',
        values: { min, label },
      }),
      max: ({ max, label }: { max: number; label?: string }) => ({
        key: 'message.shorter_than_max',
        values: { max, label },
      }),
    },
    number: {
      min: ({ min, label }: { min: number; label?: string }) => ({
        key: 'message.greater_min',
        values: { min, label },
      }),
      max: ({ max, label }: { max: number; label?: string }) => ({
        key: 'message.greater_max',
        values: { max, label },
      }),
      integer: ({ label }: { label?: string }) => ({ key: 'message.none_integer', values: { label } }),
    },
  });

  yup.addMethod<yup.NumberSchema>(yup.number, 'unitNumber', function addUnitNumberMethod(unitNumber: number, min = 0) {
    const getDecimalPoint = (num: number) => num?.toString().split('.')[1]?.length ?? 0;
    const exNumber = (unitNumber: number, min: number) =>
      Array.from(Array(3), (_, i) => ((i + 1) * unitNumber + (min || 0)).toFixed(getDecimalPoint(unitNumber))).join(
        ', '
      );

    return this.test('unitNumber', function validateUnitNumber(value?: number) {
      const { createError } = this;

      if (isNil(value)) return false;

      if (getDecimalPoint(value) <= getDecimalPoint(unitNumber)) return true;

      return createError({
        message: {
          key: 'message.insert_units',
          values: {
            unit_number: unitNumber,
            ex: exNumber(unitNumber, min),
          },
        },
      });
    });
  });

  yup.addMethod<yup.StringSchema>(
    yup.string,
    'checkConfigurationOnAutoUpdate',
    function addCheckConfigurationOnAutoUpdateMethod(
      options: CustomYup.ValidateOptions<FirmwareUpdateValidateResponse>
    ) {
      return this.test(
        'checkConfigurationOnAutoUpdate',
        async function checkConfigurationOnAutoUpdateTest(autoUpdate?: string) {
          const { createError } = this;

          if (isNull(autoUpdate)) {
            return createError({
              message: {
                key: 'message.field_required',
              },
            });
          }

          /** Auto Update가 아니라면, validation 체크를 할 필요가 없음 */
          if (autoUpdate === 'N') return true;

          try {
            const validateResult = await options.validator();
            if (validateResult.data.result === FIRMWARE_UPDATE_API_RESPONSE_CODE.DUPLICATE) {
              return createError({
                message: {
                  key: 'message.exist_stable_grp',
                  values: {
                    configuration: this.parent.systemConfiguration,
                  },
                },
              });
            }

            return true;
          } catch (e) {
            return false;
          }
        }
      );
    }
  );

  yup.addMethod<yup.StringSchema>(
    yup.string,
    'groupName',
    function addGroupNameMethod(options: CustomYup.GroupNameOptions<FirmwareUpdateValidateResponse>) {
      return this.test('groupName', async function groupNameValidationTest(groupName?: string) {
        const { createError } = this;

        if (isNull(groupName)) {
          return createError({
            message: {
              key: 'message.field_required',
            },
          });
        }

        const regex = /^[a-zA-Z0-9.,+\s\-_]+$/;
        if (!regex.test(groupName)) {
          return createError({
            message: {
              key: 'message.invalid_special_character',
            },
          });
        }

        try {
          const result = (await options.validator(groupName)) as FirmwareUpdateValidateResponse;
          if (result.data.result === FIRMWARE_UPDATE_API_RESPONSE_CODE.DUPLICATE) {
            return createError({
              message: {
                key: 'message.duplicate_invalid_grp',
              },
            });
          }

          return true;
        } catch (e) {
          return false;
        }
      });
    }
  );

  const prevValues: Record<string, CustomYup.PrevValue> = {};

  function clearPrevValues(name: string) {
    if (prevValues[name]) {
      delete prevValues[name];
    }
  }

  Object.assign(yup, { clearPrevValues });

  const isNilValueLatLon = (latitude: number, longitude: number, value?: string) =>
    isNil(value) || isNil(latitude) || isNil(longitude);

  const isSameLatLon = (latitude: number, longitude: number, { lat, lon }: { lat: number; lon: number }) =>
    latitude === lat && longitude === lon;

  yup.addMethod<yup.StringSchema>(
    yup.string,
    'isValidLatLon',
    function addIsValidLatLonMethod(
      prevValue: CustomYup.PrevValue,
      options: CustomYup.LatLonOptions,
      currValue?: CustomYup.PrevValue
    ) {
      return this.test(
        'isValidLatLon',
        { key: 'message.duplicate_location' },
        async function validateLatLon(value?: string) {
          const { createError } = this;

          const latitude = this.parent[options.latKey || 'lat'];
          const longitude = this.parent[options.lonKey || 'lon'];

          if (isNilValueLatLon(latitude, longitude, value)) {
            return createError({
              message: { key: 'message.field_required' },
            });
          }

          if (currValue && isSameLatLon(latitude, longitude, currValue.value)) {
            return currValue.status;
          }

          if (prevValue.value && isSameLatLon(latitude, longitude, prevValue.value)) {
            return prevValue.status;
          }

          try {
            const deviceService = new DeviceServiceAdmin(window.axiosInstance.axios);
            const result = await deviceService.checkDuplicatedLocation({ latitude, longitude });
            prevValue.status = !result;
            prevValue.value = { lat: latitude, lon: longitude };

            return !result;
          } catch (e) {
            return createError({
              message: {
                key: 'message.invalid',
              },
            });
          }
        }
      );
    }
  );

  yup.addMethod<yup.StringSchema>(yup.string, 'isValidName', function addIsValidNameMethod() {
    return this.test('isValidName', function nameValidationTest(name?: string) {
      const { createError } = this;

      if (isNull(name)) {
        return createError({
          message: {
            key: 'message.field_required',
          },
        });
      }

      return true;
    });
  });

  yup.addMethod<yup.StringSchema>(yup.string, 'isValidTelephoneNumber', function addIsValidTelephoneNumberMethod() {
    return this.test('isValidTelephoneNumber', function telephoneNumberValidationTest(telephone?: string) {
      const { createError } = this;

      if (isNull(telephone)) {
        return createError({
          message: {
            key: 'message.field_required',
          },
        });
      }

      const regexp = /^\d{10}$/;
      if (!regexp.test(telephone)) {
        return createError({
          message: {
            key: 'message.invalid_telephone_10_digits',
          },
        });
      }

      return true;
    });
  });

  yup.addMethod<yup.StringSchema>(yup.string, 'confirmTermination', function addConfirmTerminationMethod() {
    return this.test('confirmTermination', function confirmTerminationTest(telephone?: string) {
      const { createError } = this;

      if (isNull(telephone)) {
        return createError({
          message: {
            key: 'message.field_required',
          },
        });
      }
      const WITHDRAWAL_TEXT = 'DELETE';
      const regexp = new RegExp(`^${WITHDRAWAL_TEXT}$`);
      if (!regexp.test(telephone)) {
        return createError({
          message: {
            key: 'message.invalid',
          },
        });
      }

      return true;
    });
  });
};

export const createInstallerIdConfirmTest = (params: {
  initialValue: () => string;
  isConfirmed: () => boolean;
  emailRegex?: RegExp;
  message?: string;
}) => {
  return {
    name: 'installer-id-confirmed',
    test: (value?: string, ctx?: yup.TestContext) => {
      const current = (value || '').trim();
      const initial = (params.initialValue() || '').trim();
      const regex = params.emailRegex ?? /^[^\s@]+@[^\s@]+$/;

      if (current === initial || !regex.test(current)) return true;
      if (!params.isConfirmed()) {
        return (ctx as yup.TestContext).createError({
          message: params.message,
        });
      }

      return true;
    },
  };
};
